import React, { useState } from 'react';
import ConnectionAsk from './ConnectionAsk';
import CredentialsAsk from './CredentialsAsk';
import SnippetAsk from './SnippetAsk';
import BoundariesAsk from './BoundariesAsk';
import ActivateAsk from './ActivateAsk';
import RegisteredAsk from './RegisteredAsk';
import IdentityAsk from './IdentityAsk';
import IntentAsk from './IntentAsk';
import AccessAsk from './AccessAsk';
import AccountabilityAsk from './AccountabilityAsk';

/**
 * Whatever answers the question, rendered inside its node.
 *
 * <h3>One component, four shapes</h3>
 *
 * The spine asks six questions on the ExteLet path and six on the native one,
 * and between them they take four shapes: pick one, pick several, fill in a
 * couple of fields, say who answers. A component per question would be twelve
 * near-identical files and the twelfth would drift from the first. The shape is
 * declared by the step in data/buildScripts.js; this renders it.
 *
 * <h3>Standing answers</h3>
 *
 * A single-choice question opens with the first option already selected and a
 * multi-choice one with whatever the step preselected, so the common path is
 * one click. Nothing is submitted until the person presses the button — the
 * standing answer is a suggestion, not a decision taken on their behalf.
 */
export default function AskInput({ ask, onAnswer, busy = false }) {
  if (!ask) return null;
  if (ask.input === 'accountability') {
    return <AccountabilityAsk busy={busy} onConfirm={onAnswer} />;
  }
  /* The connection asks the backend what exists and can create one, so it is its
     own component rather than a shape this file renders. */
  /* What the agent may do, and how far it may go. Both need something only the
     platform can hand them — the discovered catalogue, and the ladders the
     enforcement bridge ranks a cap along — so each is its own component. */
  if (ask.input === 'connection') {
    return <ConnectionAsk framework={ask.framework} allowance={ask.allowance}
                          onAnswer={onAnswer} busy={busy} />;
  }
  /* The three steps after the agent exists. Each needs something only the
     platform can hand it — the minted keys, the snippet, the tenant's own
     boundary rules — so each is its own component rather than a shape here. */
  if (ask.input === 'credentials') {
    return <CredentialsAsk envelope={ask.envelope} onAnswer={onAnswer} busy={busy} />;
  }
  if (ask.input === 'snippet') {
    return <SnippetAsk snippets={ask.snippets} path={ask.path} onAnswer={onAnswer} busy={busy} />;
  }
  /* Neither of these asks anything. One reports where the registration stands
     at the end of Register; the other shows the identity the platform stamped.
     Both exist because what they show is not a shape this file renders. */
  if (ask.input === 'registered') {
    return <RegisteredAsk envelope={ask.envelope} name={ask.name} path={ask.path}
                          onAnswer={onAnswer} busy={busy} />;
  }
  /* Identity asks nothing — the platform assigned it. The component exists
     because showing a SPIFFE name, its trust domain and the claims in its
     certificate is not a shape this file renders. */
  if (ask.input === 'identity') {
    return <IdentityAsk identity={ask.identity} onAnswer={onAnswer} busy={busy} />;
  }
  if (ask.input === 'intent') {
    return <IntentAsk agentId={ask.agentId} intents={ask.intents} error={ask.intentsError}
                      scope={ask.scope} onAnswer={onAnswer} busy={busy} />;
  }
  if (ask.input === 'access') {
    return <AccessAsk agentId={ask.agentId} users={ask.users} groups={ask.groups}
                      error={ask.teamError} onAnswer={onAnswer} busy={busy} />;
  }
  if (ask.input === 'activate') {
    return <ActivateAsk registrationId={ask.registrationId} agentId={ask.agentId}
                        archetype={ask.archetype} onAnswer={onAnswer} busy={busy} />;
  }
  if (ask.input === 'boundaries') {
    return <BoundariesAsk categories={ask.categories} onAnswer={onAnswer} busy={busy} />;
  }
  if (ask.input === 'choices') return <Choices ask={ask} onAnswer={onAnswer} busy={busy} />;
  if (ask.input === 'fields') return <Fields ask={ask} onAnswer={onAnswer} busy={busy} />;
  if (ask.input === 'multi') return <Multi ask={ask} onAnswer={onAnswer} busy={busy} />;
  return <Choice ask={ask} onAnswer={onAnswer} busy={busy} />;
}

function Confirm({ label, onClick, busy }) {
  return (
    <div style={{ marginTop: 'var(--p7-space-4)' }}>
      <button type="button" className="p7-btn p7-btn--primary" disabled={busy} onClick={onClick}>
        {busy ? 'Working…' : label}
      </button>
    </div>
  );
}

const pill = (on) => ({
  borderColor: on ? 'var(--p7-accent)' : undefined,
  background: on ? 'var(--p7-accent-bg)' : undefined,
  color: on ? 'var(--p7-accent)' : undefined,
});

function Choice({ ask, onAnswer, busy }) {
  const [picked, setPicked] = useState(ask.options?.[0] ?? null);
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {(ask.options || []).map((o) => (
          <button key={o} type="button" className="p7-btn" aria-pressed={picked === o}
                  style={pill(picked === o)} onClick={() => setPicked(o)}>
            {o}
          </button>
        ))}
      </div>
      <Confirm label={ask.confirm} busy={busy} onClick={() => onAnswer(picked)} />
    </div>
  );
}

function Multi({ ask, onAnswer, busy }) {
  const [on, setOn] = useState(() => new Set(ask.preselect || []));
  const toggle = (o) => setOn((prev) => {
    const next = new Set(prev);
    if (next.has(o)) next.delete(o); else next.add(o);
    return next;
  });
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {(ask.options || []).map((o) => (
          <button key={o} type="button" className="p7-btn" aria-pressed={on.has(o)}
                  style={pill(on.has(o))} onClick={() => toggle(o)}>
            {o}
          </button>
        ))}
      </div>
      {/* Order follows the step's own list, so two people answering the same way
          send the same thing and the record does not depend on click order. */}
      <Confirm label={ask.confirm} busy={busy}
               onClick={() => onAnswer((ask.options || []).filter((o) => on.has(o)))} />
    </div>
  );
}

function Fields({ ask, onAnswer, busy }) {
  const [vals, setVals] = useState(() => Object.fromEntries(
    (ask.fields || []).map((f) => [f.key, f.value ?? '']),
  ));
  return (
    <div>
      {(ask.fields || []).map((f) => (
        <div key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: '5px',
                                  marginBottom: 'var(--p7-space-3)' }}>
          <label htmlFor={`ask-${f.key}`}
                 style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                          color: 'var(--p7-ink-muted)', fontWeight: 500 }}>
            {f.label}
          </label>
          {/* A field the console renders as a dropdown stays a dropdown here.
              Splitting it into its own spine question would reorder the
              console's own step to suit our rendering. */}
          {f.options ? (
            <select
              id={`ask-${f.key}`}
              value={vals[f.key]}
              onChange={(e) => setVals((p) => ({ ...p, [f.key]: e.target.value }))}
              style={{ padding: '9px 11px', border: '1px solid var(--p7-line-strong)',
                       borderRadius: 'var(--p7-radius-control)', font: 'var(--p7-text-body)',
                       fontFamily: 'var(--p7-font-sans)', background: 'var(--p7-surface)' }}
            >
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : (
            <input
              id={`ask-${f.key}`}
              type={f.type || 'text'}
              value={vals[f.key]}
              placeholder={f.placeholder || ''}
              onChange={(e) => setVals((p) => ({ ...p, [f.key]: e.target.value }))}
              style={{ padding: '9px 11px', border: '1px solid var(--p7-line-strong)',
                       borderRadius: 'var(--p7-radius-control)', font: 'var(--p7-text-body)',
                       fontFamily: 'var(--p7-font-sans)' }}
            />
          )}
          {f.note ? (
            <span style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                           color: 'var(--p7-ink-subtle)', lineHeight: 1.5 }}>
              {f.note}
            </span>
          ) : null}
        </div>
      ))}
      <Confirm label={ask.confirm} busy={busy} onClick={() => onAnswer(vals)} />
    </div>
  );
}

/**
 * Several single choices in one step.
 *
 * <h3>Why they are one node</h3>
 *
 * The create form types an agent on four dimensions in a single block, and they
 * are read together. Split across four nodes they would read as four unrelated
 * decisions, and the fourth would be answered having forgotten the first.
 *
 * <h3>Why a group can carry a note</h3>
 *
 * Some values are not available to a new agent — autonomous operation needs
 * Full trust, which is earned from governed runs. The reason belongs beside the
 * choice, not in the error after the whole form has been submitted.
 */
function Choices({ ask, onAnswer, busy }) {
  const groups = ask.groups || [];
  const [vals, setVals] = useState(() => Object.fromEntries(
    groups.map((g) => [g.key, g.options?.[0]?.value]),
  ));
  return (
    <div>
      {groups.map((g) => (
        <div key={g.key} style={{ display: 'flex', flexDirection: 'column', gap: '5px',
                                  marginBottom: 'var(--p7-space-3)' }}>
          <label htmlFor={`ask-${g.key}`}
                 style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                          color: 'var(--p7-ink-muted)', fontWeight: 500 }}>
            {g.label}
          </label>
          <select
            id={`ask-${g.key}`}
            value={vals[g.key]}
            onChange={(e) => setVals((p) => ({ ...p, [g.key]: e.target.value }))}
            style={{ padding: '9px 11px', border: '1px solid var(--p7-line-strong)',
                     borderRadius: 'var(--p7-radius-control)', font: 'var(--p7-text-body)',
                     fontFamily: 'var(--p7-font-sans)', background: 'var(--p7-surface)' }}
          >
            {(g.options || []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {g.note ? (
            <span style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                           color: 'var(--p7-ink-subtle)', lineHeight: 1.5 }}>
              {g.note}
            </span>
          ) : null}
        </div>
      ))}
      <Confirm label={ask.confirm} busy={busy} onClick={() => onAnswer(vals)} />
    </div>
  );
}
